/**
 * FormField — input texte avec liaison React Hook Form automatique.
 *
 *   <FormField name="email" label="Email" type="email" required />
 *
 * Utilise `useFormContext()` — la page parente doit wrapper dans
 * `<FormProvider {...form}>`. Pour les rares formulaires sans provider,
 * passer directement `register` via `<Field>` + `<Input>`.
 *
 * Adapte de scapino-portal/frontend/src/components/form/FormField.tsx.
 * Adaptation : sgraphic `<Field>` est render-prop (controlProps), donc on
 * spread `controlProps` (id, aria-*) sur l'Input.
 */
import { useEffect, useRef, type ReactNode } from 'react'
import { useFormContext, type FieldPath, type FieldValues } from 'react-hook-form'

import Field from '@/components/ui/Field'
import Input from '@/components/ui/Input'
import { getNestedError } from '@/utils/formErrors'

interface FormFieldProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>
  label: string
  required?: boolean
  /** Texte d'aide — `string` ou rich `ReactNode` (calcul dynamique). */
  helper?: ReactNode
  type?: 'text' | 'number' | 'email' | 'password' | 'url' | 'tel' | 'search' | 'date'
  placeholder?: string
  disabled?: boolean
  /** Focus l'input au montage (via ref + useEffect, pas l'attribut HTML autoFocus). */
  focusOnMount?: boolean
  step?: string
  min?: number | string
  max?: number | string
  hideLabel?: boolean
  autoComplete?: string
  inputMode?: 'text' | 'tel' | 'email' | 'numeric' | 'decimal' | 'search' | 'url' | 'none'
}

export function FormField<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  required,
  helper,
  type = 'text',
  placeholder,
  disabled,
  focusOnMount,
  step,
  min,
  max,
  hideLabel,
  autoComplete,
  inputMode,
}: FormFieldProps<TFieldValues>) {
  const {
    register,
    formState: { errors },
  } = useFormContext<TFieldValues>()

  const errorMessage = getNestedError(errors as Record<string, unknown>, name)

  const registration = register(name)

  const localRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (focusOnMount) localRef.current?.focus()
  }, [focusOnMount])

  const setRef = (el: HTMLInputElement | null) => {
    localRef.current = el
    registration.ref(el)
  }

  return (
    <Field
      label={label}
      required={required}
      helper={helper}
      error={errorMessage}
      hideLabel={hideLabel}
    >
      {(controlProps) => (
        <Input
          {...controlProps}
          {...registration}
          ref={setRef}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          step={step}
          min={min}
          max={max}
          autoComplete={autoComplete}
          inputMode={inputMode}
        />
      )}
    </Field>
  )
}

export default FormField
